import {
  KdsButton,
  KdsButtonIcon,
  KdsInput,
  KdsText,
} from "@kazaar-platform/design-system";
import { RiAddCircleFill, RiEditLine, RiUploadLine } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { type ChangeEvent, type FormEvent } from "react";
import { useFormContext } from "react-hook-form";
import { type OrderItemConfigurationSchema } from "@/components/OrderItemConfigForm/OrderItemConfigForm.schema";
import { MAX_TOTAL_QUANTITY_LENGHT } from "@/constants/orderItemQuantites.constants";
import { redispatchQuantities } from "@/helpers/redispatchQuantities";
import { type EmptyAddressBlockProps } from "./EmptyAddressBlock.types";

export const EmptyAddressBlock = ({
  onPressEditVersion,
  onPressAddAddress,
  onPressImportDispatchFile,
}: EmptyAddressBlockProps) => {
  const { t } = useTranslate();

  const methods = useFormContext<OrderItemConfigurationSchema>();

  const handleInputMaxLength = (event: FormEvent<HTMLInputElement>) => {
    if (event.currentTarget.value.length > MAX_TOTAL_QUANTITY_LENGHT) {
      event.currentTarget.value = event.currentTarget.value.slice(
        0,
        MAX_TOTAL_QUANTITY_LENGHT
      );
    }
  };

  return (
    <div className="flex flex-col gap-sp-md rounded-br-sm bg-elevation-third p-sp-md">
      {/* Quantities and Versions information */}
      <div className="flex items-center gap-sp-xs">
        <div className="flex flex-nowrap items-center gap-sp-xs">
          <KdsText color="text-bodyvariant">
            {t("orderItemConfig.totalQuantity")}
          </KdsText>
          <div className="w-20">
            <KdsInput
              size="xs"
              min={0}
              type="number"
              onInput={handleInputMaxLength}
              {...methods.register("totalQuantity", {
                valueAsNumber: true,
                onChange: (event: ChangeEvent<HTMLInputElement>) =>
                  redispatchQuantities({
                    totalQuantities: Number.parseInt(
                      event.currentTarget.value,
                      10
                    ),
                    methods: methods,
                  }),
                onBlur: (event: ChangeEvent<HTMLInputElement>) => {
                  const val = event.target.value;
                  if (val === "" || Number(val) < 0) {
                    methods.setValue("totalQuantity", 0);
                  }
                },
              })}
            />
          </div>
        </div>
        <KdsText color="text-bodyvariant">|</KdsText>
        <div className="flex flex-nowrap items-center gap-sp-xs">
          <KdsText color="text-bodyvariant">
            {t("orderItemConfig.totalVersion")}
          </KdsText>
          <KdsText size="subtitle-sm">
            {methods.getValues("versions").filter((v) => !v.isDeleted).length}
          </KdsText>
        </div>
        <KdsButtonIcon
          variant="outlined"
          icon={RiEditLine}
          size="small"
          onPress={onPressEditVersion}
        />
      </div>

      {/* Dispatch Action Buttons */}
      <div className="flex items-center justify-center gap-sp-md rounded-br-lg bg-elevation-second p-sp-md">
        <KdsButton
          iconLeft={RiAddCircleFill}
          onPress={onPressAddAddress}
          dataCy="addAddressButton"
        >
          {t("buttons.createNewAddress")}
        </KdsButton>
        <KdsButton
          iconLeft={RiUploadLine}
          variant="outlined"
          onPress={onPressImportDispatchFile}
        >
          {t("buttons.uploadFile")}
        </KdsButton>
      </div>
    </div>
  );
};
